import { useEffect } from "react";
import { UserRejectedRequestError } from "viem";
import { useConfig, useConnect } from "wagmi";
import type { Connector } from "wagmi";

import { isLedgerProvider, LEDGER_RDNS } from "@constants/ledger";

/**
 * Ledger injects its provider asynchronously, and wagmi samples for it exactly
 * once: `Hydrate` calls `reconnect()` from its mount effect and never retries.
 * A provider that lands a moment later is missed until the next page load,
 * which is why a refresh used to be needed.
 */
const PROVIDER_TIMEOUT_MS = 10_000;
const PROVIDER_POLL_MS = 250;
const CONNECT_ATTEMPTS = 3;
const CONNECT_RETRY_MS = 500;

const wait = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Connects the dApp automatically when it is running inside Ledger's DApp
 * Browser, where the wallet is already unlocked and a connect prompt is noise.
 *
 * wagmi's own mount reconnect handles returning users, whose session is
 * persisted. This hook covers the cold path, where the provider may not have
 * been injected yet by the time that single pass runs.
 */
const useAutoConnect = () => {
  const config = useConfig();
  const { connectAsync } = useConnect();

  useEffect(() => {
    let cancelled = false;

    const isConnected = () => config.state.status === "connected";

    /**
     * Ledger can surface twice — through EIP-6963 discovery and through the
     * injected fallback — so prefer the discovered one, whose id is the rdns
     * the rest of the app keys its Ledger UI off. Only injected connectors are
     * probed; calling `getProvider()` on a WalletConnect connector would spin
     * up its client as a side effect.
     */
    const findLedgerConnector = async () => {
      const candidates = config.connectors
        .filter((connector) => connector.type === "injected")
        .sort(
          (a, b) => Number(b.id === LEDGER_RDNS) - Number(a.id === LEDGER_RDNS)
        );

      for (const candidate of candidates) {
        const provider = await candidate.getProvider().catch(() => undefined);
        if (isLedgerProvider(provider)) {
          return candidate;
        }
      }

      return undefined;
    };

    const waitForLedgerConnector = async () => {
      const deadline = Date.now() + PROVIDER_TIMEOUT_MS;

      while (!cancelled && !isConnected() && Date.now() < deadline) {
        const connector = await findLedgerConnector();
        if (connector) {
          return connector;
        }
        // Polls rather than waits on an event: the provider can arrive either
        // by injection or by an EIP-6963 announcement, and wagmi only registers
        // the latter once its persisted store has rehydrated.
        await wait(PROVIDER_POLL_MS);
      }

      return undefined;
    };

    const connectLedger = async (connector: Connector) => {
      for (let attempt = 0; attempt < CONNECT_ATTEMPTS; attempt += 1) {
        if (cancelled || isConnected()) {
          return;
        }

        try {
          await connectAsync({ connector });
          return;
        } catch (error) {
          // The user said no. Retrying would re-prompt them.
          if (
            error instanceof UserRejectedRequestError ||
            (error as { code?: number })?.code === UserRejectedRequestError.code
          ) {
            return;
          }
          // Anything else is treated as the provider not being ready yet:
          // wagmi's mount reconnect may hold the connection, or Ledger may not
          // answer `eth_requestAccounts` on a cold load.
          await wait(CONNECT_RETRY_MS);
        }
      }
    };

    void (async () => {
      const connector = await waitForLedgerConnector();
      if (cancelled || !connector) {
        return;
      }
      await connectLedger(connector);
    })();

    return () => {
      cancelled = true;
    };
  }, [config, connectAsync]);
};

export default useAutoConnect;
